import styled from 'styled-components';
import SwitchiOS from '../../../SwitchiOS';
import { useChartContext } from '../../ChartContext';
import { YAxisPosition } from '../../interfaces';
import {
  AssignmentLabel,
  AssignmentRowWrapper,
} from '../CentralChartSettingUI';
import { useAppDispatch } from '../../../../redux/hooks';
import {
  setChartSettingYAxisConfigAction,
  toggleIsApplyYAxisFormatAction,
} from '../../../../redux/slices/analyses';
import SegmentSelect from '../../../SegmentSelect';
import { MeasureDisplayFormatKind } from '../../../../utils/apollo/graphqlSchema';
import { SvgIcon } from '../../../Icon';
import { theme } from '../../../../styles/theme';
import { formatValueWithYAxisFormat } from '../../../../utils/formatting/measureFormatting';
import KubitTooltip from '../../../KubitTooltip';
import { checkIfMeasureFormatAppliedOnYAxis } from '../utils';
import { generateFormatOption, getYAxisLabelByPosition } from './utils';
import { globalFormat } from '../../../../utils/formatting';
import { MAX_FORMAT_DECIMALS } from '../../../../config/constants';
import toastMessage from '../../../../utils/toastMessage';

interface IProps {
  yAxisPosition: YAxisPosition;
}

const YAxisFormatLabel = styled(AssignmentLabel)`
  width: 140px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledSegmentSelect = styled(SegmentSelect<MeasureDisplayFormatKind>)`
  flex: 1;
`;

const DecimalButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
`;

const DecimalButton = styled.button`
  display: flex;
  height: 24px;
  flex: 1;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  border-radius: 2px;
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.secondary400};
  :hover {
    cursor: pointer;
    background: ${theme.colors.blue500};
    color: ${theme.colors.mainBlue};
    border: 1px solid ${theme.colors.mainBlue};

    > svg {
      fill: ${theme.colors.mainBlue};
    }
  }
`;

const YAxisFormatAssignments = ({ yAxisPosition }: IProps) => {
  const dispatch = useAppDispatch();
  const {
    metadata: { analysisId },
    settings: {
      chartSettings: { axisSettings },
    },
    displayedMeasures,
  } = useChartContext();

  const currentYAxisSettings = axisSettings.yAxisSettings[yAxisPosition];

  const isMeasureFormatAppliedOnYAxis = checkIfMeasureFormatAppliedOnYAxis({
    displayedMeasures,
    yAxisPosition,
  });

  const onToggleApplyYAxisFormat = () => {
    dispatch(
      toggleIsApplyYAxisFormatAction({
        analysisId,
        yAxisPosition,
      }),
    );
  };

  const onFormatKindChange = (value: MeasureDisplayFormatKind) => {
    dispatch(
      setChartSettingYAxisConfigAction({
        analysisId,
        yAxisPosition,
        config: { format: { ...currentYAxisSettings.format, kind: value } },
      }),
    );
  };

  const onDecimalsChange = (action: 'decrease' | 'increase') => {
    const updatedDecimalPlace =
      currentYAxisSettings.format.decimalPlace +
      (action === 'increase' ? 1 : -1);
    if (updatedDecimalPlace > MAX_FORMAT_DECIMALS) {
      toastMessage({
        message: `Decimal places cannot be greater than ${MAX_FORMAT_DECIMALS}`,
        type: 'error',
      });
      return;
    }
    // prevent negative decimal places
    if (updatedDecimalPlace >= 0) {
      dispatch(
        setChartSettingYAxisConfigAction({
          analysisId,
          yAxisPosition,
          config: {
            format: {
              ...currentYAxisSettings.format,
              decimalPlace: updatedDecimalPlace,
            },
          },
        }),
      );
    }
  };

  return (
    <>
      <AssignmentRowWrapper>
        <YAxisFormatLabel>
          <span>Apply Y-Axis Format</span>
          {isMeasureFormatAppliedOnYAxis && (
            <KubitTooltip
              content={
                `The measure on the ${getYAxisLabelByPosition(yAxisPosition)} Y-Axis has a format. ` +
                `Enabling ${getYAxisLabelByPosition(yAxisPosition)} Y-Axis format will override it.`
              }
            >
              <SvgIcon name="info" width={16} height={16} />
            </KubitTooltip>
          )}
        </YAxisFormatLabel>
        <SwitchiOS
          isChecked={currentYAxisSettings.isApplyYAxisFormat}
          onClick={onToggleApplyYAxisFormat}
        />
      </AssignmentRowWrapper>

      {currentYAxisSettings.isApplyYAxisFormat && (
        <>
          <AssignmentRowWrapper>
            <AssignmentLabel>Unit</AssignmentLabel>
            <StyledSegmentSelect
              options={generateFormatOption(
                globalFormat.getFormatCurrencySymbol(),
              )}
              value={currentYAxisSettings.format.kind}
              onChange={onFormatKindChange}
            />
          </AssignmentRowWrapper>
          <AssignmentRowWrapper>
            <AssignmentLabel>Decimals</AssignmentLabel>
            <DecimalButtonWrapper>
              <DecimalButton onClick={() => onDecimalsChange('decrease')}>
                <SvgIcon
                  name="arrow-left"
                  width={16}
                  height={16}
                  fill={theme.colors.secondary400}
                />
                .0
              </DecimalButton>
              <DecimalButton onClick={() => onDecimalsChange('increase')}>
                .00
                <SvgIcon
                  name="arrow-right"
                  width={16}
                  height={16}
                  fill={theme.colors.secondary400}
                />
              </DecimalButton>
            </DecimalButtonWrapper>
          </AssignmentRowWrapper>
          <AssignmentRowWrapper $justifyContent="flex-start">
            <AssignmentLabel>Preview</AssignmentLabel>
            <AssignmentLabel>
              {formatValueWithYAxisFormat(1.23, currentYAxisSettings)}
            </AssignmentLabel>
          </AssignmentRowWrapper>
        </>
      )}
    </>
  );
};

export default YAxisFormatAssignments;
